import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {Parameter} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetCommentsQuery, useGetNoteQuery} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetWorkshopInterestQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {customerRoutes, testIds} from '@dms/routes';
import {
  DetailTemplate,
  EMPTY_PLACEHOLDER,
  EntityHeaderFlag,
  getCustomerEmail,
  getCustomerName,
  getCustomerPhoneNumber,
  isCustomerNaturalPerson,
  queryParams,
  Section,
  SnippetProps,
  type SectionItem,
} from '@dms/shared';

import {buildArray, composePath, parseDate, useQueryState, useRequiredParams} from 'shared/web';

import {ActionLog} from './(sections)/ActionLog/ActionLog';
import {ContactPerson} from './(sections)/ContactPerson/ContactPerson';
import {Overview} from './(sections)/Overview/Overview';
import {WorkshopInterestActivityPanel} from './components/WorkshopInterestActivityPanel';
import {useWorkshopInterestActions} from './hooks/useWorkshopInterestActions';

const WORKSHOP_INTEREST_SECTIONS = {
  overview: 'overview',
  contactPerson: 'contactPerson',
  ownership: 'ownership',
  serviceRequest: 'serviceRequest',
  checklist: 'checklist',
  tasks: 'tasks',
  actionLog: 'actionLog',
} as const;

export function WorkshopInterestDetail() {
  const {id: interestId} = useRequiredParams();
  const formatDateTime = useDateTimeFormatter();
  const [, setActiveSection] = useQueryState(queryParams.COMPONENT_SECTIONS_SECTION);

  const {
    data: workshopInterest,
    isLoading,
    isError,
  } = useGetWorkshopInterestQuery({id: interestId});

  const {data: customer} = useGetCustomerV2Query(
    isNotNil(workshopInterest?.contactPersonId)
      ? {customerId: workshopInterest.contactPersonId}
      : skipToken
  );

  const {data: note} = useGetNoteQuery({
    resourceId: EntityResourceIds.workshopInterest,
    recordId: interestId,
    context: EntityResourceIds.workshopInterest,
  });

  const {data: comments = []} = useGetCommentsQuery({
    resourceId: EntityResourceIds.workshopInterest,
    recordId: interestId,
  });

  const actions = useWorkshopInterestActions(interestId);

  const title = getCustomerName(customer) ?? EMPTY_PLACEHOLDER;

  const flags = buildArray<EntityHeaderFlag>()
    .when(workshopInterest?.state === 'NEW', {
      colorScheme: 'blue',
      label: i18n.t('general.labels.new'),
      isSubtle: true,
    })
    .when(workshopInterest?.state === 'OPEN', {
      colorScheme: 'green',
      label: i18n.t('general.actions.open'),
      isSubtle: true,
    })
    .when(workshopInterest?.state === 'UNSUCCESSFUL', {
      colorScheme: 'red',
      label: i18n.t('general.labels.unsuccessful'),
      isSubtle: true,
    })
    .add({
      colorScheme: 'orange',
      label: i18n.t('page.workshop.labels.interest'),
      isSubtle: false,
    });

  const onNoteCommentIconClick = () => {
    setActiveSection(WORKSHOP_INTEREST_SECTIONS.overview);
  };

  const parameters = buildArray<Parameter>()
    .when(isNotNilOrEmpty(note?.text), {
      icon: 'action/description',
      title: null,
      variant: 'secondary',
      tooltip: note?.text,
      onClick: onNoteCommentIconClick,
    })
    .when(comments.length > 0, {
      icon: 'communication/comment',
      title: null,
      variant: 'secondary',
      hideSeparator: true,
      onClick: onNoteCommentIconClick,
    })
    .when(isNotNil(workshopInterest?.publicId), workshopInterest?.publicId)
    .when(
      isNotNil(workshopInterest?.createdAt),
      `${i18n.t('general.labels.created')}: ${
        isNotNil(workshopInterest?.createdAt)
          ? formatDateTime('dateTimeShort', parseDate(workshopInterest.createdAt))
          : EMPTY_PLACEHOLDER
      }`
    );

  const snippets = buildArray<SnippetProps>().add({
    'data-testid': testIds.workshop.interestDetail('contactPerson-snippet'),
    resourceId: EntityResourceIds.workshopInterest,
    recordId: interestId,
    label: i18n.t('entity.customer.labels.customer'),
    placeholder: {
      title: i18n.t('entity.customer.labels.customer'),
    },
    title: getCustomerName(customer) ?? EMPTY_PLACEHOLDER,
    parameters: buildArray<string | null>()
      .when(
        isNotNil(customer) && !isCustomerNaturalPerson(customer),
        getCustomerPhoneNumber(customer)
      )
      .when(isNotNil(customer) && !isCustomerNaturalPerson(customer), getCustomerEmail(customer))
      .when(
        isNotNil(customer) && isCustomerNaturalPerson(customer),
        getCustomerPhoneNumber(customer)
      )
      .when(isNotNil(customer) && isCustomerNaturalPerson(customer), getCustomerEmail(customer)),
    href: customer?.id
      ? composePath(customerRoutes.detail, {
          params: {id: customer.id},
          isSearchParamsPreserved: false,
        })
      : undefined,
    customerId: customer?.id,
    selectedContactId: customer?.foundingPerson?.id,
  });

  const sections = buildArray<SectionItem>()
    .add({
      id: WORKSHOP_INTEREST_SECTIONS.overview,
      label: i18n.t('general.labels.overview'),
      'data-testid': testIds.workshop.interestDetail('section-overview'),
      content: <Overview interestId={interestId} />,
    })
    .add({
      id: WORKSHOP_INTEREST_SECTIONS.contactPerson,
      label: i18n.t('entity.customer.labels.contactPerson'),
      'data-testid': testIds.workshop.interestDetail('section-contactPerson'),
      content: <ContactPerson interestId={interestId} />,
    })
    .add({
      id: WORKSHOP_INTEREST_SECTIONS.ownership,
      label: i18n.t('entity.customer.labels.ownership'),
      'data-testid': testIds.workshop.interestDetail('section-ownership'),
      content: <Section />,
    })
    .add({
      id: WORKSHOP_INTEREST_SECTIONS.serviceRequest,
      label: i18n.t('entity.order.labels.serviceRequest'),
      'data-testid': testIds.workshop.interestDetail('section-serviceRequest'),
      content: <Section />,
      hasSeparator: true,
    })
    .add({
      id: WORKSHOP_INTEREST_SECTIONS.checklist,
      label: i18n.t('entity.checklist.labels.title'),
      'data-testid': testIds.workshop.interestDetail('section-checklist'),
      content: <Section />,
    })
    .add({
      id: WORKSHOP_INTEREST_SECTIONS.tasks,
      label: i18n.t('entity.task.labels.tasks'),
      'data-testid': testIds.workshop.interestDetail('section-tasks'),
      content: <Section />,
      hasSeparator: true,
    })
    .add({
      id: WORKSHOP_INTEREST_SECTIONS.actionLog,
      label: i18n.t('general.labels.activityLogs'),
      'data-testid': testIds.workshop.interestDetail('section-actionLog'),
      content: <ActionLog />,
    });

  return (
    <>
      <Helmet title={i18n.t('page.workshop.labels.interest')} />
      <DetailTemplate
        key={workshopInterest?.id}
        isLoading={isLoading}
        isError={isError}
        activityPanel={<WorkshopInterestActivityPanel interestId={interestId} />}
        header={{
          title,
          icon: 'content/next_week',
          flags,
          parameters,
          snippets,
          recordId: interestId,
          resourceId: EntityResourceIds.workshopInterest,
          controls: ['WORKSHOP_INTEREST_BRANCH', 'WORKSHOP_INTEREST_SOURCE', 'ASSIGNEE'],
          actions,
        }}
        sections={sections}
        data-testid={testIds.workshop.interestDetail('page')}
        permissions={{
          tag: {
            isReadable: true,
            isCreatable: true,
            isUpdatable: true,
            isDeletable: true,
            isAssignable: true,
            isUnassignable: true,
          },
        }}
      />
    </>
  );
}
